import { Icon } from './Icon.tsx';
import type { DhaftharuMeta, ColumnSpec, BizEntity } from '../features/dhaftharu/api.ts';
import { dv } from '../i18n/dv.ts';

// ── encode / decode ───────────────────────────────────────────────────────────
// FULLNAME | STATUS | HOUSE:<attr> | RESIDENT_FIELD:<id>
// BUSINESS_FIELD:REGISTERED_BUSINESS:<id> | BUSINESS_FIELD:HOME_BUSINESS:<id>

function encodeColumnSpec(col: ColumnSpec): string {
  switch (col.source) {
    case 'FULLNAME': return 'FULLNAME';
    case 'STATUS':   return 'STATUS';
    case 'HOUSE':    return `HOUSE:${col.attr ?? ''}`;
    case 'RESIDENT_FIELD': return `RESIDENT_FIELD:${col.fieldId ?? ''}`;
    case 'BUSINESS_FIELD': return `BUSINESS_FIELD:${col.entity ?? ''}:${col.fieldId ?? ''}`;
    default: return '';
  }
}

function parseColumnSpec(encoded: string): ColumnSpec | null {
  if (encoded === 'FULLNAME') return { source: 'FULLNAME' };
  if (encoded === 'STATUS')   return { source: 'STATUS' };
  if (encoded.startsWith('HOUSE:')) {
    const attr = encoded.slice('HOUSE:'.length) as 'name' | 'number' | 'address' | 'ward';
    return { source: 'HOUSE', attr };
  }
  if (encoded.startsWith('RESIDENT_FIELD:')) {
    const fieldId = encoded.slice('RESIDENT_FIELD:'.length);
    if (!fieldId) return null;
    return { source: 'RESIDENT_FIELD', fieldId };
  }
  const rbPrefix = 'BUSINESS_FIELD:REGISTERED_BUSINESS:';
  if (encoded.startsWith(rbPrefix)) {
    const fieldId = encoded.slice(rbPrefix.length);
    if (!fieldId) return null;
    return { source: 'BUSINESS_FIELD', entity: 'REGISTERED_BUSINESS' as BizEntity, fieldId };
  }
  const hbPrefix = 'BUSINESS_FIELD:HOME_BUSINESS:';
  if (encoded.startsWith(hbPrefix)) {
    const fieldId = encoded.slice(hbPrefix.length);
    if (!fieldId) return null;
    return { source: 'BUSINESS_FIELD', entity: 'HOME_BUSINESS' as BizEntity, fieldId };
  }
  return null;
}

function resolveColumnLabel(col: ColumnSpec, meta: DhaftharuMeta): string {
  switch (col.source) {
    case 'FULLNAME': return dv.dhaftharu.colName;
    case 'STATUS':   return dv.dhaftharu.colStatus;
    case 'HOUSE': {
      const hc = meta.houseColumns.find((c) => c.attr === col.attr);
      return hc?.label ?? col.attr ?? '';
    }
    case 'RESIDENT_FIELD': {
      const f = meta.residentFields.find((f) => f.id === col.fieldId);
      return f?.label ?? col.fieldId ?? '';
    }
    case 'BUSINESS_FIELD': {
      const fields: typeof meta.registeredBusinessFields =
        col.entity === 'REGISTERED_BUSINESS'
          ? meta.registeredBusinessFields
          : meta.homeBusinessFields;
      const f = fields.find((f) => f.id === col.fieldId);
      return f?.label ?? col.fieldId ?? '';
    }
    default: return '';
  }
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface ColumnPickerProps {
  meta: DhaftharuMeta;
  value: ColumnSpec[];
  onChange: (cols: ColumnSpec[]) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ColumnPicker({ meta, value, onChange }: ColumnPickerProps): JSX.Element {
  const existingKeys = new Set(value.map(encodeColumnSpec));

  const move = (index: number, dir: -1 | 1): void => {
    const next = [...value];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    const a = next[index];
    const b = next[target];
    if (a === undefined || b === undefined) return;
    next[index] = b;
    next[target] = a;
    onChange(next);
  };

  const remove = (index: number): void => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleAdd = (encoded: string): void => {
    if (!encoded) return;
    const spec = parseColumnSpec(encoded);
    if (!spec) return;
    onChange([...value, spec]);
  };

  return (
    <div className="space-y-3">
      {/* Chosen columns list */}
      {value.length === 0 ? (
        <p className="text-sm text-muted-foreground">{dv.dhaftharu.noColumns}</p>
      ) : (
        <ol className="card-paper divide-y divide-border overflow-hidden">
          {value.map((col, i) => (
            <li key={i} className="flex items-center gap-3 p-3">
              <span className="mono-meta tabular w-6 shrink-0 text-muted-foreground">{i + 1}</span>
              <p className="min-w-0 flex-1 text-sm font-medium leading-snug truncate">
                {resolveColumnLabel(col, meta)}
              </p>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  aria-label={dv.forms.moveUp}
                  className="btn-ghost px-2 py-1 disabled:opacity-30"
                >
                  <Icon name="expand_less" size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === value.length - 1}
                  aria-label={dv.forms.moveDown}
                  className="btn-ghost px-2 py-1 disabled:opacity-30"
                >
                  <Icon name="expand_more" size={15} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  aria-label={dv.common.remove}
                  className="btn-ghost px-2 py-1 text-destructive hover:text-destructive"
                >
                  <Icon name="delete" size={15} />
                </button>
              </div>
            </li>
          ))}
        </ol>
      )}

      {/* Add column select */}
      <div className="flex items-center gap-2">
        <Icon name="add" size={15} className="shrink-0 text-muted-foreground" />
        <select
          className="input flex-1"
          value=""
          onChange={(e) => handleAdd(e.target.value)}
          aria-label={dv.dhaftharu.addColumn}
        >
          <option value="">{dv.dhaftharu.pickColumnPlaceholder}</option>

          {/* Fixed columns */}
          {!existingKeys.has('FULLNAME') && (
            <option value="FULLNAME">{dv.dhaftharu.colName}</option>
          )}
          {!existingKeys.has('STATUS') && (
            <option value="STATUS">{dv.dhaftharu.colStatus}</option>
          )}

          {/* House columns */}
          {meta.houseColumns.filter((hc) => !existingKeys.has(`HOUSE:${hc.attr}`)).length > 0 && (
            <optgroup label={dv.dhaftharu.groupHouse}>
              {meta.houseColumns
                .filter((hc) => !existingKeys.has(`HOUSE:${hc.attr}`))
                .map((hc) => (
                  <option key={hc.attr} value={`HOUSE:${hc.attr}`}>
                    {hc.label}
                  </option>
                ))}
            </optgroup>
          )}

          {/* Resident fields */}
          {meta.residentFields.filter((f) => !existingKeys.has(`RESIDENT_FIELD:${f.id}`)).length > 0 && (
            <optgroup label={dv.dhaftharu.groupResident}>
              {meta.residentFields
                .filter((f) => !existingKeys.has(`RESIDENT_FIELD:${f.id}`))
                .map((f) => (
                  <option key={f.id} value={`RESIDENT_FIELD:${f.id}`}>
                    {f.label}
                  </option>
                ))}
            </optgroup>
          )}

          {/* Registered business fields */}
          {meta.registeredBusinessFields.filter(
            (f) => !existingKeys.has(`BUSINESS_FIELD:REGISTERED_BUSINESS:${f.id}`),
          ).length > 0 && (
            <optgroup label={dv.dhaftharu.groupRegisteredBiz}>
              {meta.registeredBusinessFields
                .filter((f) => !existingKeys.has(`BUSINESS_FIELD:REGISTERED_BUSINESS:${f.id}`))
                .map((f) => (
                  <option key={f.id} value={`BUSINESS_FIELD:REGISTERED_BUSINESS:${f.id}`}>
                    {f.label}
                  </option>
                ))}
            </optgroup>
          )}

          {/* Home business fields */}
          {meta.homeBusinessFields.filter(
            (f) => !existingKeys.has(`BUSINESS_FIELD:HOME_BUSINESS:${f.id}`),
          ).length > 0 && (
            <optgroup label={dv.dhaftharu.groupHomeBiz}>
              {meta.homeBusinessFields
                .filter((f) => !existingKeys.has(`BUSINESS_FIELD:HOME_BUSINESS:${f.id}`))
                .map((f) => (
                  <option key={f.id} value={`BUSINESS_FIELD:HOME_BUSINESS:${f.id}`}>
                    {f.label}
                  </option>
                ))}
            </optgroup>
          )}
        </select>
      </div>
    </div>
  );
}
